// @givmo/app-admin — sign-up JOIN-OR-CREATE pivot carry-over (EP-SUITE2).
//
// When the onboarding resolve (provider path) or the COMPANY_DOMAIN_JOINABLE create-fork (email path)
// determines the caller's work-email domain already belongs to an ACTIVE company, the flow pivots to
// the request-access screen instead of creating a duplicate. That screen needs the target company's
// display identity (name + logo), which the triggering screen already holds — this small store carries
// it across the sign-up flow's navigations (at the pivot the email path CANNOT re-resolve to recover
// it, because its work email is not yet server-verified). It also remembers whether the join request
// was filed this session, so an in-session revisit shows the pending view rather than the request
// button. Render-only state — no auth logic. A hard refresh clears it — with no target the
// request-access screen then re-resolves for a VERIFIED caller with join-or-create on
// (EP-SUITE2-FIX3) and restarts anyone else at /sign-up; leaving a signed-in uid clears it — a
// sign-out, or one account replacing another in this tab (`app.tsx`, the identity boundary).
//
// ★ THE STORE LIVES OUTSIDE THE ROUTER TREE, AT MODULE SCOPE — BY CONSTRUCTION (EP-SUITE2-FIX2 (a)).
// The email path CREATES the Firebase user mid-flow, and a uid change is the app's hard cache
// boundary: `RoutedApp` (`app.tsx`) remounts the WHOLE `RouterProvider` (`key={cacheEpoch}`) the
// moment `auth.user.uid` changes, so every provider inside the route tree — the sign-up subtree's
// included — is rebuilt with fresh state while the registration POST is still in flight. A store
// held in React state under that tree therefore died between the write and the read: the
// email/password screen's continuation wrote the target into the OLD provider's state, the NEW tree's
// request-access screen mounted reading `null`, and its "no target → restart" effect bounced the
// requester to `/sign-up` (observed on live staging 2026-09-09, reproduced against a local build of
// the fixed bundle before this cure). The provider path never trips it — its uid was set before the
// details screen — which is why the surface looked fine. A module-level store survives the remount
// (and nothing else about the boundary changes: the query cache still clears, the guards still
// re-run). Writes are synchronous and reads go through `useSyncExternalStore`, whose mount read is
// `getSnapshot()` — so a mount also cannot read a value older than the last write, whatever React
// lane the writer was on (`sign-up-join-context.test.tsx` pins both properties; never cure either
// with a timeout).

import { createContext, use, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { CompanyJoinTarget } from '@givmo/auth';

/** The store's snapshot — immutable; a write mints a new object so `useSyncExternalStore` sees it. */
export interface SignUpJoinState {
  /** The company a request-access pivot points at; null until a pivot occurs. */
  readonly target: CompanyJoinTarget | null;
  /** Whether the join request was filed this session (a revisit then shows the pending view). */
  readonly filed: boolean;
}

/** The external store: synchronous writes, subscribable reads. */
export interface SignUpJoinStoreHandle {
  getSnapshot: () => SignUpJoinState;
  subscribe: (listener: () => void) => () => void;
  /** Point the pivot at a company. A NEW target starts a fresh request state (`filed` resets). */
  setTarget: (target: CompanyJoinTarget | null) => void;
  setFiled: (filed: boolean) => void;
  /** Back to the initial state — the identity boundary (leaving a signed-in uid) and tests call this. */
  reset: () => void;
}

/** What a screen consumes (unchanged shape): the current values + the two writers. */
export interface SignUpJoinStore extends SignUpJoinState {
  setTarget: (target: CompanyJoinTarget | null) => void;
  setFiled: (filed: boolean) => void;
}

const INITIAL_STATE: SignUpJoinState = { target: null, filed: false };

/** Create a join store. The app uses ONE, at module scope; tests may build their own for isolation. */
export function createSignUpJoinStore(): SignUpJoinStoreHandle {
  let state = INITIAL_STATE;
  const listeners = new Set<() => void>();
  const write = (next: SignUpJoinState) => {
    if (next.target === state.target && next.filed === state.filed) return; // no-op: same snapshot
    state = next;
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setTarget: (target) =>
      write({ target, filed: target !== null && target === state.target ? state.filed : false }),
    setFiled: (filed) => write({ target: state.target, filed }),
    reset: () => write(INITIAL_STATE),
  };
}

/** The app's one store — deliberately above the router tree (see the header). Exported for tests. */
export const appSignUpJoinStore: SignUpJoinStoreHandle = createSignUpJoinStore();

/** Clear the app's store (leaving a signed-in uid: a sign-out or an account switch; a test's `beforeEach`). */
export function resetSignUpJoinStore(): void {
  appSignUpJoinStore.reset();
}

const SignUpJoinContext = createContext<SignUpJoinStoreHandle>(appSignUpJoinStore);

/**
 * Test seam: scope a store to a subtree. The app itself renders NO provider — its store is the
 * module-level one, precisely so the router tree's remounts cannot reset it.
 */
export function SignUpJoinProvider({
  store,
  children,
}: {
  store: SignUpJoinStoreHandle;
  children: ReactNode;
}) {
  return <SignUpJoinContext value={store}>{children}</SignUpJoinContext>;
}

/** Read the sign-up join-pivot store (the app's, or a test-scoped one). */
export function useSignUpJoin(): SignUpJoinStore {
  const store = use(SignUpJoinContext);
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return useMemo<SignUpJoinStore>(
    () => ({ ...state, setTarget: store.setTarget, setFiled: store.setFiled }),
    [store, state],
  );
}

export { SignUpJoinContext };
